import { ApiError } from '../api/client'
import type {
  ApiCheckout,
  ApiOrder,
  ExpressCheckItem,
  ExpressCheckResult,
  ExpressConfirmationBody,
} from '../api/types'
import { HTTP_STATUS } from '../constants/api'
import {
  EXPRESS_CONFIRMATION_REQUIRED,
  EXPRESS_REASON,
} from '../constants/express'
import type { CartLine } from './cart'

export function cartVariantIds(lines: CartLine[]): string[] {
  return [...new Set(lines.map((line) => line.variantId).filter(Boolean))]
}

export function downgradedExpressItems(
  items: ExpressCheckItem[],
): ExpressCheckItem[] {
  return items.filter((item) => item.reason === EXPRESS_REASON.noVendorInRange)
}

export function isExpressOrder(order: { isExpress?: boolean }): boolean {
  return order.isExpress === true
}

export function readExpressConfirmation(
  error: unknown,
): ExpressConfirmationBody | null {
  if (!(error instanceof ApiError) || error.status !== HTTP_STATUS.badRequest) {
    return null
  }
  const body = unwrapErrorBody(error.body)
  if (!isExpressConfirmationBody(body)) return null
  return body
}

export function confirmationFromExpressCheck(
  result: ExpressCheckResult,
): ExpressConfirmationBody {
  return {
    code: EXPRESS_CONFIRMATION_REQUIRED,
    message: result.message,
    requiresConfirmation: true,
    canSplit: false,
    items: result.items,
    groups: result.groups,
    summary: result.summary,
    radiusMeters: result.radiusMeters,
  }
}

export function readCheckoutOrders(payload: ApiCheckout | ApiOrder): ApiOrder[] {
  if ('orders' in payload && Array.isArray(payload.orders) && payload.orders.length > 0) {
    return payload.orders
  }
  if ('id' in payload && 'orderNumber' in payload && 'items' in payload) {
    return [payload as ApiOrder]
  }
  return []
}

export function siblingCheckoutOrders(
  orders: ApiOrder[] | undefined,
  current: ApiOrder,
  placedIds: string[] = [],
): ApiOrder[] {
  const list = orders ?? []
  if (placedIds.length > 1) {
    const byId = new Map(list.map((order) => [order.id, order]))
    return placedIds
      .map((id) => byId.get(id))
      .filter((order): order is ApiOrder => Boolean(order && order.id !== current.id))
  }
  if (!current.checkoutId) return []
  return list.filter(
    (order) => order.checkoutId === current.checkoutId && order.id !== current.id,
  )
}

function unwrapErrorBody(payload: unknown): unknown {
  if (!payload || typeof payload !== 'object') return payload
  const record = payload as { message?: unknown; code?: unknown }
  if (record.code === EXPRESS_CONFIRMATION_REQUIRED) return payload
  if (record.message && typeof record.message === 'object') return record.message
  return payload
}

function isExpressConfirmationBody(
  value: unknown,
): value is ExpressConfirmationBody {
  if (!value || typeof value !== 'object') return false
  const record = value as Partial<ExpressConfirmationBody>
  return (
    record.code === EXPRESS_CONFIRMATION_REQUIRED &&
    record.requiresConfirmation === true &&
    Array.isArray(record.items)
  )
}
